import React, { useState, useEffect } from 'react';
import { Dialog } from '@mui/material';
import { Button } from '../../Button';
import useStyles from './styles';
import { get, size, first, orderBy } from 'global/lib/lodash';
import clsx from 'clsx';
import colors from '../../../styles/colors';
import { executePrint, getRealmName, isNativeAppBrowser, saveWorksheet, sortAlphaNum } from '../../../utils';
import { useView } from '../../../hooks/useView';
import useBodyOverflow from 'global/hooks/useBodyOverflow';
import sendData from 'redux/actions/sendData';
import moment from 'moment';
import * as arrayToTree from 'array-to-tree';
import { getItemType, isGob } from '../../../../feature/report/AssessmentScore/utils';
import ExcelJS from 'exceljs';

const PrintRow = (props) => {
  const { li, first, pagebreak } = props;

  const classes = useStyles();

  return (
    <>
      {!Object.keys(li).length ? (
        <></>
      ) : (
        <div className={first || !pagebreak ? classes.pagebreakfirst : classes.pagebreak}>
          {!!li.title && <div style={get(li, 'titleStyle', {})}>{li.title}</div>}
          <div style={li.style}>{li.render({ onLoaded: props.onLoaded })}</div>
        </div>
      )}
    </>
  );
};

export const testNativeSelect = () => {
  return new Promise((resolve, reject) => {
    if (isNativeAppBrowser()) {
      const link = window.location.origin+window.location.pathname;
      get(window, 'TLM.nativeAlert', () => 0)({ 
        message: `This functionality is not available on the app. Please visit ${link} to use.`,
        link,
        buttons: [
          { text: 'Okay' },
          { text: 'Visit Link', action: 'link' },
        ]
      });
      return reject();
    }

    resolve();
  })
}

export const PrintView = (props) => {
  //items come over as { render: () => {}, style: {{}} }
  const { items, title, button, onClose, onOpen, assessment, pagebreak } = props;

  const { view, toggleTheme } = useView();
  const [open, setOpen] = useState(false);
  const [toggleBack, setToggleBack] = useState(false);
  const [loaded, setLoaded] = useState({});
  const { setBodyOverflow } = useBodyOverflow();
  const [exp, setExp] = useState({
    loading: false,
  });

  const classes = useStyles();

  const toggleRoot = (hide = true) => {
    const root = document.getElementById('root');
    root.style.display = hide ? 'none' : 'block';
    if (!hide) {
      setBodyOverflow();
    }
  };

  const theme = get(view, 'style.palette.mode', 'light');

  useEffect(() => {
    toggleRoot(open);
    if (open && theme === 'dark') {
      toggleTheme();
      setToggleBack(true);
    } else if (!open && toggleBack) {
      toggleTheme();
      setToggleBack(false);
    }
    if (!open && onClose) {
      onClose();
    } else if (open && onOpen) {
      onOpen();
    }
    if (!open) {
      setLoaded({});
    }
  }, [open]);

  const onPrint = () => {
    testNativeSelect().then(() => {
      if (open) {
        executePrint();
      } else {
        setOpen(true);
      }
    })
  };

  const onExport = async () => {
    try {
      setExp((s) => ({ ...s, loading: true }));

      const _id = get(assessment, 'id');

      const scdata = await Promise.all(
        get(assessment, 'scorecards', []).map(
          async (scard) =>
            await Promise.all([
              sendData(
                { url: `/processed-scorecard-score-values` },
                {
                  normalize: { build: ['processedScorecardScoreValues'] },
                  params: {
                    filter: { assessment_id: _id, scorecard_id: get(scard, 'id') },
                    include: 'checklist,checkpoint,subScores,subScores.images,subScores.realm',
                  },
                }
              ),
              first(
                await sendData(
                  { url: `/scorecards/${scard.id}` },
                  {
                    normalize: { build: ['scorecards'] },
                    params: {
                      filter: { viewable: true, withInactive: true },
                      include: 'checklists,checklists.images,checklists.checkpoints',//,checklists.checkpoints.scorecardOptions',
                    },
                  }
                )
              ),
              sendData(
                { url: `/processed-scorecard-score-values` },
                {
                  normalize: { build: ['processedScorecardScoreValues'] },
                  params: {
                    filter: { assessment_id: _id, scorecard_id: get(scard, 'id'), status: 'goal' },
                  },
                }
              ),
            ])
        )
      );

      const getuid = (i) => {
        if (i.depth === 2 && !!i.scorecardScoreId) return;
        return `${i.scorecardId}_${i.systemId}_${i.supersetId}_${i.checklistId}_${i.checkpointId}`;
      };

      const hasNewTargets = (get(assessment, 'availableStatus', []) || []).includes('goal');

      const book = new ExcelJS.Workbook();
      const sheet = book.addWorksheet('Sheet');

      const rowcount = hasNewTargets ? 9 : 8;
      const createRow = (arr = []) => {
        const ret = [];
        for (let i = 0; i < rowcount; i++) {
          ret.push(arr[i] !== undefined ? arr[i] : '');
        }
        return ret;
      };

      const itemSeperator = ' | ';

      const aname = assessment.name;

      const displayRows = [
        createRow([aname]),
        createRow(),
        createRow(['Criteria', scdata.map((s) => get(s, '1.name')).join(itemSeperator)]),
        createRow(['Location', getRealmName(assessment, '', true)]),
        createRow(['Author', get(assessment, 'authors.0.name')]),
        createRow(['Editors', assessment.editors.map((a) => a.name).join(itemSeperator)]),
        createRow(['Viewers', assessment.viewers.map((a) => a.name).join(itemSeperator)]),
        createRow(['Created At', moment(get(assessment, 'createdAt')).format('L')]),
        createRow(['Downloaded At', moment().format('L')]),
        createRow(),
      ];

      const tableProps = {
        name: 'MyTable',
        ref: 'A12',
        headerRow: true,
        style: {
          showRowStripes: true,
        },
        columns: [
          { name: 'System', filterButton: true, width: 100 },
          { name: 'Checklist', filterButton: true, width: 100 },
          { name: 'Checkpoint', filterButton: true, width: 100 },
          { name: 'Description', filterButton: true, width: 100 },
          { name: 'Notes', filterButton: true, width: 100 },
          { name: 'Instance', filterButton: true, width: 100 },
          { name: 'Score', filterButton: true, width: 100 },
          ...(hasNewTargets ? [{ name: 'New Target', filterButton: true, width: 100 }] : []),
          { name: 'ID', filterButton: true, width: 100 },
        ],
        rows: [],
      }

      let currentcard;
      const subDisplayScore = (s) => {
        const map = {
          '0': get(get(currentcard, 'config.subScoreValueLabel', []).find(f => f.value == 0), 'label', 'FAIL'),
          '-1': get(get(currentcard, 'config.subScoreValueLabel', []).find(f => f.value == -1), 'label', 'NOTE'),
          '100': get(get(currentcard, 'config.subScoreValueLabel', []).find(f => f.value == 100), 'label', 'PASS')
        }

        return get(map, parseInt(s.score), 'NOTE');
      }

      let targetMap = {};

      console.log({ scdata })

      const loopRows = (r, parents = []) => {
        const type = getItemType(r);
        const isgob = isGob(r);
        const id = (() => {
          const v = ['supersetId', 'checklist.classificationId', 'checkpoint.grandestId'].map(k => get(r, k)).filter(f => !!f);
          if (isgob) {
            v.push('0');
          }
          return v.join('-');
        })();

        const uid = getuid(r);
        const targetScore = uid ? get(targetMap, uid) : null;
        const targetDisplay = get(targetScore, 'displayValue', get(targetScore, 'displayScore', ''));

        if (type === 'superset') {
          tableProps.rows.push(createRow([r.name, 'OVERALL', '', '', '', '', r.displayScore, ...(hasNewTargets ? [targetDisplay] : []), id]));
        } else if (type === 'checklist') {
          tableProps.rows.push(createRow([get(parents, '0.name'), r.name, 'OVERALL', '', '', '', r.displayScore, ...(hasNewTargets ? [targetDisplay] : []), id]));
        } else if (isgob) {
          tableProps.rows.push(createRow([get(parents, '0.name'), get(parents, '1.name'), 'GENERAL OBSERVATIONS', '', get(r, 'criteria', ''), '', r.displayScore, ...(hasNewTargets ? [targetDisplay] : []), id]));
        } else if (type === 'checkpoint') {
          const subs = get(r, 'subScores', []);

          if (size(subs)) {
            tableProps.rows.push(createRow([get(parents, '0.name'), get(parents, '1.name'), r.name, get(r, 'criteria', ''), get(r, 'notes', ''), '', r.displayScore, ...(hasNewTargets ? [targetDisplay] : []), id]));
            subs.map(s => {
              tableProps.rows.push(createRow([get(parents, '0.name'), get(parents, '1.name'), r.name, get(r, 'criteria', ''), get(s, 'notes', ''), 'yes', subDisplayScore(s), ...(hasNewTargets ? [''] : []), id]));
            })
          } else {
            tableProps.rows.push(createRow([get(parents, '0.name'), get(parents, '1.name'), r.name, get(r, 'criteria', ''), get(r, 'notes', ''), '', r.displayScore, ...(hasNewTargets ? [targetDisplay] : []), id]));
          }
        }

        if (r.children) {
          const childOrder =
            type === 'checklist'
              ? r.children.sort((a, b) => {
                  if (!a.checkpointId) {
                    return -1;
                  }
                  if (!b.checkpointId) {
                    return 1;
                  }
                  return sortAlphaNum(a, b);
                })
              : orderBy(r.children, 'order');
          childOrder.map((c) => loopRows(c, [...parents, r]));
        }
      };

      scdata.map((sc) => {
        const [scores, scorecard, targets] = sc;
        const treed = arrayToTree(scores, { parentProperty: 'parentId' });

        // Build target score lookup by uid
        targetMap = {};
        (targets || []).forEach((t) => {
          const uid = getuid(t);
          if (uid) targetMap[uid] = t;
        });

        displayRows.push(createRow([scorecard.name]));
        displayRows.map(r => sheet.addRow(r));

        currentcard = scorecard;

        treed.map((t) => {
          orderBy(t.children, 'order').map((c) => loopRows(c));
        });
      });

      [
        { id: 1, width: 30 },
        { id: 2, width: 40 },
        { id: 3, width: 20 },
        { id: 4, width: 50 },
        { id: 5, width: 50 },
        { id: 6, width: 10 },
        { id: 7, width: 10 },
        ...(hasNewTargets ? [{ id: 8, width: 12 }] : []),
        { id: hasNewTargets ? 9 : 8, width: 15 },
      ].map(r => sheet.getColumn(r.id).width = r.width);

      sheet.addTable(tableProps);

      const buffer = await book.xlsx.writeBuffer();
      saveWorksheet(`${new Date().toISOString()}_${aname}_export.xlsx`, buffer)
    } catch (err) {
      alert(err);
    } finally {
      setExp((s) => ({ ...s, loading: false }));
    }
  };

  const extraClick = (e) => {
    testNativeSelect().then(() => {
      get(button, 'onClick', () => 0)(e);
      onPrint(e);
    })
  };

  const onLoaded = (li, i) => {
    setLoaded((l) => ({ ...l, [i]: true }));
  };

  const renderButtons = () => {
    const printText = get(props, 'printBtn.text', open ? 'Print' : `Print ${size(items)}`);
    const exportBtn = get(props, 'exportBtn');
    const printStyle = get(props, 'printBtn.style', {});
    const useLoaded = get(props, 'useLoaded', false);
    const loadComplete = Object.keys(loaded).length >= size(items);

    const disabled = !useLoaded ? false : open ? !loadComplete : false;

    return !size(items) ? null : (
      <div style={!open ? props.buttonContainerStyle : {}} className={clsx(classes.buttonContainer, open && classes.buttonContainerFixed)}>
        {button && !open && (
          <Button onClick={extraClick} className={clsx(classes.button, classes.extraButton)}>
            {get(button, 'title', 'Print All')}
          </Button>
        )}
        {open && (
          <Button className={classes.button} bgColor={colors.red} onClick={() => setOpen(false)}>
            {'Close'}
          </Button>
        )}
        {!!exportBtn && !open && (
          <Button {...(exportBtn || {})} onClick={onExport} loading={exp.loading} style={get(exportBtn, 'style', {})} className={classes.button}>
            {get(exportBtn, 'text')}
          </Button>
        )}
        <Button className={classes.button} style={open ? {} : printStyle} disabled={disabled} onClick={onPrint}>
          {printText}
        </Button>
      </div>
    );
  };

  const hideCoverPage = get(props, 'hideCoverPage', false);

  return (
    <>
      {!!open && <Dialog className={classes.dialog} fullScreen={true} open={open}>
        <div className={classes.fauxPage}>
          {!hideCoverPage && (
            <div className={clsx(classes.contents)}>
              <div className={classes.title}>{title}</div>
              {items.map((item, i) => {
                return <div className={classes.coverTitle}>{item.coverTitle}</div>;
              })}
            </div>
          )}

          {items.map((li, i) => (
            <PrintRow key={i} first={i == 0 && hideCoverPage ? true : false} li={li} pagebreak={pagebreak} onLoaded={() => onLoaded(li, i)} />
          ))}
        </div>

        {renderButtons()}
      </Dialog>}

      {renderButtons()}
    </>
  );
};
